import api from '../lib/api';
import { toArray } from '../lib/utils';
import type {
  Email,
  SentEmail,
  EmailCredential,
  WhiteBlackListEntry,
  EmailSearchFilters,
  PaginatedResponse,
  EmailFolder,
} from '../types/email';

class EmailService {
  // Email Management
  async searchEmails(filters: EmailSearchFilters): Promise<PaginatedResponse<Email>> {
    const response = await api.get('/email/search', { params: filters });
    return response.data;
  }

  async markAsRead(id: number, isRead: boolean): Promise<void> {
    await api.put(`/email/${id}/read`, { is_read: isRead });
  }

  async toggleStar(id: number): Promise<void> {
    await api.put(`/email/${id}/star`);
  }

  async deleteEmail(id: number): Promise<void> {
    await api.delete(`/email/${id}`);
  }

  async markAsSpam(id: number): Promise<void> {
    await api.post(`/email/${id}/spam`);
  }

  async replyToEmail(id: number, data: FormData): Promise<{ id: number }> {
    const response = await api.post(`/email/${id}/reply`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async sendEmail(data: FormData): Promise<{ id: number }> {
    const response = await api.post('/email/send', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getSentEmails(
    type?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<SentEmail>> {
    const response = await api.get('/email/sent', { params: { type, page, limit } });
    return response.data;
  }

  async deleteSentEmail(id: number): Promise<void> {
    await api.delete(`/email/sent/${id}`);
  }

  // Email Details
  async getReceivedEmailDetails(id: number): Promise<{ email: Email; replies: SentEmail[] }> {
    const response = await api.get(`/email/received/${id}`);
    return response.data;
  }

  async getSentEmailDetails(id: number): Promise<{ email: SentEmail; originalEmail?: Email }> {
    const response = await api.get(`/email/sent/${id}/details`);
    return response.data;
  }

  // Email Folders
  async getFolders(): Promise<EmailFolder[]> {
    const response = await api.get('/email/folders');
    return toArray(response.data);
  }

  async createFolder(data: Partial<EmailFolder>): Promise<EmailFolder> {
    const response = await api.post('/email/folders', data);
    return response.data;
  }

  async updateFolder(id: number, data: Partial<EmailFolder>): Promise<EmailFolder> {
    const response = await api.put(`/email/folders/${id}`, data);
    return response.data;
  }

  async deleteFolder(id: number): Promise<void> {
    await api.delete(`/email/folders/${id}`);
  }

  async moveToFolder(emailId: number, folderId: number): Promise<void> {
    await api.post(`/email/${emailId}/move`, { folder_id: folderId });
  }

  // Email Credentials
  async getCredentials(): Promise<EmailCredential[]> {
    const response = await api.get('/email/credentials');
    return toArray(response.data);
  }

  async createCredential(data: Partial<EmailCredential>): Promise<EmailCredential> {
    const response = await api.post('/email/credentials', data);
    return response.data;
  }

  async updateCredential(id: number, data: Partial<EmailCredential>): Promise<EmailCredential> {
    const response = await api.put(`/email/credentials/${id}`, data);
    return response.data;
  }

  async deleteCredential(id: number): Promise<void> {
    await api.delete(`/email/credentials/${id}`);
  }

  // White/Black List
  async getWhiteBlackList(search?: string): Promise<WhiteBlackListEntry[]> {
    const response = await api.get('/email/list', { params: { search } });
    return toArray(response.data);
  }

  async addToList(
    data: Omit<WhiteBlackListEntry, 'id' | 'created_at'>
  ): Promise<WhiteBlackListEntry> {
    const response = await api.post('/email/list', data);
    return response.data;
  }

  async updateListEntry(
    id: number,
    data: Partial<WhiteBlackListEntry>
  ): Promise<WhiteBlackListEntry> {
    const response = await api.put(`/email/list/${id}`, data);
    return response.data;
  }

  async deleteListEntry(id: number): Promise<void> {
    await api.delete(`/email/list/${id}`);
  }
}

export default new EmailService();
